import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle, RefreshCw,
  SlidersHorizontal, Search, RotateCcw, ClipboardCheck,
} from "lucide-react";
import { Button }    from "@/components/ui/Button";
import { Select }    from "@/components/ui/Select";
import { DateInput } from "@/components/ui/DateInput";
import { useModules } from "@/context/ModulesContext";

import {
  computeOrcadoRealizadoPorCentro,
  computeDistribuicaoOrcado,
  computeTopInsumos,
  computeAllInsumos,
  filterByUnidadeConstrutiva,
  listUnidadesConstrutivas,
  ucLabel,
} from "./chartUtils";

import { OrcadoVsRealizadoPorCentro } from "./charts/OrcadoVsRealizadoPorCentro";
import { TopInsumosChart }            from "./charts/TopInsumosChart";
import { DistribuicaoOrcamento }      from "./charts/DistribuicaoOrcamento";
import { InsumoDetalheTable }         from "./charts/InsumoDetalheTable";
import { SolicitacaoOrcamentoModal }  from "./SolicitacaoOrcamentoModal";

/* ─── Página ─────────────────────────────────────────────────────────────── */
export function OrcamentoPage() {
  const { orcamento, ccOptions, loadingCC } = useModules();
  const {
    buildingId, setBuildingId,
    startDate, setStartDate,
    endDate, setEndDate,
    applied, apply, applyWith, reset,
    didAutoSelect,
    data, loading, error,
  } = orcamento;

  const [selectedUC, setSelectedUC] = useState<string>("all");
  const [analiseScOpen, setAnaliseScOpen] = useState(false);

  /* Auto-seleciona o primeiro CC quando a lista carrega (só na primeira vez). */
  useEffect(() => {
    if (!didAutoSelect.current && ccOptions.length > 0) {
      didAutoSelect.current = true;
      const first = ccOptions[0].value;
      applyWith({ buildingId: first, startDate, endDate });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccOptions]);

  useEffect(() => {
    setSelectedUC("all");
  }, [applied?.buildingId]);

  const ucSelectOptions = useMemo(() => {
    if (!data) {
      return [{ value: "all", label: "Todas as unidades construtivas" }];
    }
    const ucs = listUnidadesConstrutivas(data.budgetResources, data.appropriated);
    const redundant = new Set([
      "all",
      "todas",
      "todas as unidades construtivas",
      "todas as unidades de imóvel",
      "todas as unidades de imovel",
      "todas as unidades construtivas",
    ]);
    const cleaned = ucs.filter((u) => !redundant.has(u.trim().toLowerCase()));

    const idsByName = new Map<string, Set<string>>();
    for (const r of [...data.budgetResources, ...data.appropriated]) {
      const name = ucLabel(r.buildingUnitName ?? "");
      const id = String(r.buildingUnitId ?? "").trim();
      if (!idsByName.has(name)) idsByName.set(name, new Set());
      if (id) idsByName.get(name)!.add(id);
    }

    return [
      { value: "all", label: "Todas as unidades construtivas" },
      ...cleaned.map((u) => {
        const ids = [...(idsByName.get(u) ?? [])].sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true }),
        );
        const idSuffix = ids.length > 0 ? ` · ${ids.join(", ")}` : "";
        return { value: u, label: `${u}${idSuffix}` };
      }),
    ];
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data) return null;
    return {
      budgetResources: filterByUnidadeConstrutiva(data.budgetResources, selectedUC),
      appropriated:    filterByUnidadeConstrutiva(data.appropriated, selectedUC),
    };
  }, [data, selectedUC]);

  const isFirstLoad = loading && !data;

  const porCentro    = useMemo(
    () =>
      filteredData
        ? computeOrcadoRealizadoPorCentro(
            filteredData.budgetResources,
            filteredData.appropriated,
          )
        : [],
    [filteredData],
  );
  const distribuicao = useMemo(() => {
    if (!filteredData) return [];
    const raw   = computeDistribuicaoOrcado(filteredData.budgetResources);
    const total = raw.reduce((s, r) => s + r.value, 0);
    return raw.map((r) => ({ ...r, pct: total > 0 ? (r.value / total) * 100 : 0 }));
  }, [filteredData]);
  const topInsumos = useMemo(
    () =>
      filteredData
        ? computeTopInsumos(
            filteredData.budgetResources,
            filteredData.appropriated,
            15,
          )
        : [],
    [filteredData],
  );
  const allInsumos = useMemo(
    () =>
      filteredData
        ? computeAllInsumos(filteredData.budgetResources, filteredData.appropriated)
        : [],
    [filteredData],
  );

  const hasCharts      = data || loading;
  const selectedCCName = ccOptions.find((p) => p.value === applied?.buildingId)?.label;
  const ucFilterLabel  = selectedUC === "all"
    ? null
    : ucSelectOptions.find((o) => o.value === selectedUC)?.label ?? selectedUC;

  return (
    <div className="flex flex-col gap-6">

      {/* ── Cabeçalho ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Orçamento</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Orçado × Realizado dos insumos por unidade de imóvel
            {selectedCCName && (
              <span className="ml-2 text-brand-600 font-semibold">— {selectedCCName}</span>
            )}
            {ucFilterLabel && (
              <span className="ml-2 text-slate-600 font-medium">
                · filtro: {ucFilterLabel}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAnaliseScOpen(true)}
            disabled={!applied?.buildingId}
            leftIcon={<ClipboardCheck className="w-4 h-4" />}
          >
            Análise de solicitação
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={apply}
            loading={loading}
            leftIcon={<RefreshCw className="w-4 h-4" />}
            disabled={!buildingId}
          >
            Atualizar
          </Button>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-card px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <SlidersHorizontal className="w-3.5 h-3.5 text-brand-600" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Filtros
          </span>
        </div>

        <div
          className="flex flex-wrap items-end gap-3"
          onKeyDown={(e) => e.key === "Enter" && apply()}
        >
          <div className="flex-[2] min-w-[220px]">
            <Select
              label="Obra / Empreendimento"
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
              options={ccOptions}
              loading={loadingCC}
              placeholder="Selecione a obra…"
            />
          </div>

          <div className="flex-[2] min-w-[220px]">
            <Select
              label="Unidade Construtiva"
              value={selectedUC}
              onChange={(e) => setSelectedUC(e.target.value)}
              options={ucSelectOptions}
              disabled={!data}
              placeholder="Todas…"
            />
          </div>

          <div className="hidden sm:block w-px h-10 bg-slate-200 self-end mb-0.5" />

          <div className="flex-1 min-w-[140px] max-w-[180px]">
            <DateInput
              label="Período — Início"
              value={startDate}
              onChange={setStartDate}
              max={endDate || undefined}
            />
          </div>
          <div className="flex-1 min-w-[140px] max-w-[180px]">
            <DateInput
              label="Período — Fim"
              value={endDate}
              onChange={setEndDate}
              min={startDate || undefined}
            />
          </div>

          <div className="flex items-end gap-2 self-end">
            <Button
              variant="primary"
              size="md"
              onClick={apply}
              loading={loading}
              disabled={!buildingId}
              leftIcon={<Search className="w-4 h-4" />}
            >
              Aplicar
            </Button>
            <Button
              variant="ghost"
              size="md"
              onClick={() => {
                setSelectedUC("all");
                reset();
              }}
              title="Limpar filtros"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!applied?.buildingId && (
          <p className="mt-3 text-xs text-amber-600 font-medium">
            Selecione uma obra para carregar os dados de orçamento.
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={apply}
            className="ml-auto text-red-500 hover:text-red-700 underline text-xs"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {hasCharts && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Detalhamento por Insumo
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <InsumoDetalheTable
            data={allInsumos}
            appropriated={filteredData?.appropriated ?? []}
            loading={isFirstLoad}
            applied={applied ?? null}
          />

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Top insumos — Orçado × Realizado
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <TopInsumosChart data={topInsumos} loading={isFirstLoad} />
        </>
      )}

      {isFirstLoad && (
        <div className="text-center py-4">
          <p className="text-sm text-slate-500">
            Consultando o Sienge… aguarde enquanto carregamos os dados de orçamento.
          </p>
        </div>
      )}

      {hasCharts && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Demais análises
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
            <OrcadoVsRealizadoPorCentro data={porCentro} loading={isFirstLoad} />
            <DistribuicaoOrcamento data={distribuicao} loading={isFirstLoad} />
          </div>
        </>
      )}

      <SolicitacaoOrcamentoModal
        open={analiseScOpen}
        onClose={() => setAnaliseScOpen(false)}
        filterBuildingId={applied?.buildingId ?? ""}
        startDate={applied?.startDate ?? startDate}
        endDate={applied?.endDate ?? endDate}
      />
    </div>
  );
}
